import React from 'react';
import {Space, Tooltip} from 'antd';
import {Link} from 'react-router-dom';
import {
  StyledCheckedCircleIconContainer,
  StyledJournalEntriesTabsIconSpace,
  StyledJournalEntriesTabsInput,
  StyledJournalEntriesTabsSelect
} from '../../components/pages/NetSuiteJournalEntriesPage/JournalEntriesTabs/StyledJournalEntriesTabs';
import {
  StyledMissingErpTabIcon,
  StyledMissingErpTabIconsSpace
} from '../../components/pages/NetSuiteBillsPage/tabs/MissingErpTab/StyledMissingErpTab';
import MissingInfo from '../../components/MissingInfo';
import StatusMark from '../../components/StatusMark';
import UserDetails from '../../components/UserDetails';
import InvoiceFilePreviewWithIcon from '../../components/pages/NetSuiteJournalEntriesPage/InvoiceFilePreviewWithIcon';
import InvoicePreviewWithIcon from '../../components/pages/NetSuiteJournalEntriesPage/InvoicePreviewWithIcon';
import {
  billInvoiceSyncStatusConstants,
  customSegmentLevelTypeConstants,
  expenseTypesConstants,
  journalEntryLevelConstants,
  journalEntryStatusesConstants,
  journalEntrySyncStatusConstants,
  vendorSyncStatusConstants
} from '../../constants';
import {alertActions} from '../../state/actions/alert.actions';
import {amountHelpers} from './amount.helpers';
import {formHelpers} from './form.helpers';
import {dateHelpers} from './date.helpers';
import {invoicesHelpers} from './invoices.helpers';
import {objectHelpers} from './object.helpers';
import {tabHelpers} from './tab.helpers';
import {vendorsHelpers} from './vendors.helpers';
import {CheckIcon, ExternalLinkIcon, WithLessCreditCardIcon} from '../../icons';

const {READY_FOR_SUBMISSION, READY_FOR_REVIEW} = journalEntryStatusesConstants;

const journalEntrySyncStatuses = {
  [journalEntrySyncStatusConstants.IN_PROGRESS]: {
    key: 'journalEntrySyncStatuses.inProgress',
    color: '#FFCC00'
  },
  [journalEntrySyncStatusConstants.ERROR]: {
    key: 'journalEntrySyncStatuses.error',
    color: '#FF4D4F'
  },
  [journalEntrySyncStatusConstants.SUCCESS]: {
    key: 'journalEntrySyncStatuses.success',
    color: '#52C41A'
  },
};

const vendorSyncStatuses = {
  [vendorSyncStatusConstants.ERROR]: {
    key: 'vendorSyncStatuses.error',
    color: '#FF4D4F'
  },
  [vendorSyncStatusConstants.IN_PROGRESS]: {
    key: 'vendorSyncStatuses.inProgress',
    color: '#FFCC00'
  },
  [vendorSyncStatusConstants.SUCCESS]: {
    key: 'vendorSyncStatuses.success',
    color: '#52C41A'
  },
};

export const SyncStatusOptions = () => formHelpers.getStatusOptionsList(vendorSyncStatuses);

export const accountingHelpers = {

  getDateColumnProps: () => ({
    render: (value) => value ? dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD') : '',
    sortDirections: ['descend', 'ascend'],
    sorter: true,
    width: 150,
  }),

  getBillInvoiceDate: (value) => {
    const date = value ? dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD') : '';
    return <span className='no-wrap'>{date}</span>;
  },

  getBillInvoiceLink: ({invoice, t} = {}) => (
    <Space className='ant-table-cell-actions' size='small' wrap={false}>
      {accountingHelpers.getInvoiceLinkIcon({id: invoice?.backend_invoice_id, t})}
    </Space>
  ),

  getBillInvoiceDateWithLink: ({invoice, t} = {}) => (
    <StyledMissingErpTabIconsSpace size='middle' wrap={false}>
      {accountingHelpers.getBillInvoiceDate(invoice?.invoice_date)}
      {accountingHelpers.getBillInvoiceLink({invoice, t})}
    </StyledMissingErpTabIconsSpace>
  ),

  getBillInvoiceNumberWithLink: ({invoice, t} = {}) => (
    <StyledMissingErpTabIconsSpace size='middle' wrap={false}>
      {invoice?.custbody_nexil_numb_vendorbillrefnum || ''}
      {accountingHelpers.getBillInvoiceLink({invoice, t})}
    </StyledMissingErpTabIconsSpace>
  ),

  getBillTaxCode: (data) => Array.isArray(data?.item) && data.item.length ? data.item[0]?.tax_code : '',

  getBillListItem: (data) => Array.isArray(data?.item) && data.item.length ? data.item[0]?.item : undefined,

  getBillVendorName: (_, record) => record?.vendor?.name || '',

  getCreditAccount: (data) => {
    const line = (data?.lines || []).find(i => Number(i.line_credit) > 0);
    return line?.account ?? null;
  },

  getDebitAccount: (data) => {
    const line = (data?.lines || []).find(i => Number(i.line_debit) > 0);
    return line?.account ?? null;
  },

  getDepartment: (data) => {
    return data?.lines?.[0]?.department ?? null;
  },

  getEntryAmountValue: (data) => {
    const firstLine = data?.lines?.[0] || {};
    return firstLine.line_credit || firstLine.line_debit || 0;
  },

  getEntryAmount: (data) => {
    const currency = data?.currency || '';
    const amount = accountingHelpers.getEntryAmountValue(data);
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, undefined, currency)}
      </span>
    );
  },

  getBillAmount: (_, data) => {
    const currency = data?.payment_currency ?? data?.currency ?? '';
    const amount = data?.total_payment_amount ?? data?.total_amount ?? 0;
    return (
      <span className='amount'>
        {amountHelpers.getAmountWithCurrencyCode(amount, undefined, currency)}
      </span>
    );
  },

  getEntryCardId: (value) => value && (
    <StyledJournalEntriesTabsIconSpace size='small' wrap={false} className='justify-start'>
      <WithLessCreditCardIcon /><span>{value}</span>
    </StyledJournalEntriesTabsIconSpace>
  ),

  getValue: ({linesPropName = 'lines', levels = [], name, record} = {}) => {
    const level = Array.isArray(levels) ? levels[0] : levels;

    if (level === journalEntryLevelConstants.LINES) {
      return record?.[linesPropName]?.[0]?.custom_segments?.[name] || undefined;
    }
    if (level === journalEntryLevelConstants.HEADER) {
      return record?.custom_segments?.[name] || undefined;
    }

    return record?.[name] || undefined;
  },

  getUpdateData: ({entry = {}, levels = [], propName, value} = {}) => {
    let data = levels.length === 0 ? { [propName]: value } : {};

    const levelsData = {
      [journalEntryLevelConstants.HEADER]: {
        header_custom_segments: {
          ...(entry?.custom_segments || {}),
          [propName]: value,
        }
      },
      [journalEntryLevelConstants.LINES]: {
        lines_custom_segments: {
          ...(entry?.lines?.[0]?.custom_segments || {}),
          [propName]: value,
        }
      }
    };

    levels.forEach(level => {
      data = {
        ...data,
        ...(levelsData[level] || {})
      }
    });

    return data;
  },

  getEntryTransactionDate: (value, record) => (
    <Space size='small' wrap={false}>
      {accountingHelpers.renderReadyToSyncIcon(record)}
      <span className='no-wrap'>
        {dateHelpers.getDateWithMonth(value, 'YYYY-MM-DD')}
      </span>
    </Space>
  ),

  getOwnerDetails: ({value, employees = []} = {}) => {
    const owner = employees.find(e => e.employee_id === value);
    if (!owner) return '';
    return <UserDetails email={false} user={owner} />;
  },

  getTableInput: ({defaultValue, onChange, propName, placeholderPropName, record, t, ...rest}) => (
    <StyledJournalEntriesTabsInput
      defaultValue={defaultValue}
      debounceTime={2000}
      onDebouncedChange={(value) => onChange?.(value)}
      placeholder={t?.('placeholderTemplates.enter', {name: t?.(placeholderPropName || propName.replaceAll('_', ' ')).toLowerCase()})}
      variant='borderless'
      {...rest}
    />
  ),

  getTableSelect: ({defaultValue, onSelect, options, propName, placeholderPropName, record, t, ...rest}) => (
    <StyledJournalEntriesTabsSelect
      defaultValue={defaultValue}
      styles={{ popup: { root: { minWidth: 150, maxWidth: 300, width: 'max-content' } } }}
      filterOption={(input, option) => option?.label?.toLowerCase().includes(input.toLowerCase())}
      popupMatchSelectWidth={false}
      placeholder={t?.('placeholderTemplates.select', {name: t?.(placeholderPropName || propName.replaceAll('_', ' ')).toLowerCase()})}
      options={options}
      onSelect={(value) => onSelect?.(value)}
      showSearch={true}
      variant='borderless'
      {...rest}
    />
  ),

  getSyncStatus: (_, data) => {
    const { sync_status: statusCode, sync_message: syncMessage } = data || {};
    const statusMark = <StatusMark statuses={journalEntrySyncStatuses} statusCode={statusCode} />;
    return statusCode === journalEntrySyncStatusConstants.ERROR
      ? <Tooltip title={syncMessage}>{statusMark}</Tooltip>
      : statusMark;
  },

  getVendorName: ({exist = true, data, t} = {}) => {
    if (!exist) return <MissingInfo description={t('isNotDefined', {name: t('vendor')})} />;

    const vendorName = data?.vendor?.name;

    return vendorsHelpers.isVendorAccountingReference(data.vendor)
      ? vendorName
      : (
        <Space size={0} direction='vertical'>
          <span>{vendorName}</span>
          <MissingInfo description={t?.('isNew', { name: t('vendor') })} />
        </Space>
      );
  },

  allCustomSegmentsFilled: ({data, customSegments = [], linesPropName = 'lines', levelsPropName = ''} = {}) => {
    if (customSegments.length) {
      const values = customSegments.map(segment =>
        accountingHelpers.getValue({
          linesPropName, levels: segment?.setup?.[levelsPropName], name: segment?.setup?.internal_id, record: data
        })
      );
      return values.every(v => v !== null && v !== undefined && v !== '');
    }
    return true;
  },

  isEntryReadyToSync: (data, customSegments = []) => {
    const status = data?.status;
    if (![READY_FOR_SUBMISSION, READY_FOR_REVIEW].includes(status)) return false;

    const requitedFields = [];
    const hasRequiredFields = objectHelpers.areAllRequiredFieldsFilled(data, requitedFields);
    if (!hasRequiredFields) return false;

    const allCustomSegmentsFilled = accountingHelpers.allCustomSegmentsFilled({
      data, customSegments, linesPropName: 'lines', levelsPropName: customSegmentLevelTypeConstants.JE
    });
    if (!allCustomSegmentsFilled) return false;

    const hasDebit = accountingHelpers.getDebitAccount(data);
    const hasCredit = accountingHelpers.getCreditAccount(data);
    const hasVendor = data?.expense ? data?.expense?.expense_type === expenseTypesConstants.INVOICE ? vendorsHelpers.isVendorAccountingReference(data?.vendor) : true : false;
    const hasInvoice = !accountingHelpers.isMissingInvoice(data);
    return [hasDebit, hasCredit, hasVendor, hasInvoice].every(Boolean);
  },

  isInvoiceReadyToSync: (data, customSegments = []) => {
    const requitedFields = [];
    const hasRequiredFields = objectHelpers.areAllRequiredFieldsFilled(data, requitedFields);
    if (!hasRequiredFields) return false;

    const allCustomSegmentsFilled = accountingHelpers.allCustomSegmentsFilled({
      data, customSegments, linesPropName: 'item', levelsPropName: customSegmentLevelTypeConstants.VENDOR_BILL
    });
    if (!allCustomSegmentsFilled) return false;

    const hasListItem = accountingHelpers.getBillListItem(data);
    const hasTaxCode = accountingHelpers.getBillTaxCode(data);
    const hasVendor = vendorsHelpers.isVendorAccountingReference(data?.vendor);
    return [hasListItem, hasVendor, hasTaxCode, hasRequiredFields].every(Boolean);
  },

  isVendorReadyToSync: (data) => {
    const requitedFields = ['tax_number', 'country', 'address', 'vendor_tax_code', 'default_numbering_flow'];
    return objectHelpers.areAllRequiredFieldsFilled(data, requitedFields);
  },

  formatInvoice: ({customSegments = [], invoice, employees = [], vendors = []} = {}) => {
    invoice = {
      ...invoice,
      vendor: vendors.find((v) => v.vendor_id === invoice.vendor_id),
    }
    if (!invoice.uploaded_by) {
      invoice = {
        ...invoice,
        uploaded_by: employees.find(e => e.employee_id === invoice?.uploaded_by_user_id)?.email || '',
      }
    }

    return {
      ...invoice,
      is_ready_to_sync: accountingHelpers.isInvoiceReadyToSync(invoice, customSegments)
    };
  },

  formatJournalEntry: ({customSegments = [], entry, expensesMap = {}, invoicesMap = {}, vendors = []}) => {
    entry = {
      ...entry,
      expense: expensesMap[entry.expense_id] || null,
      invoice: invoicesMap[entry.invoice_id] || null,
      vendor: vendors.find((v) => v.vendor_id === entry.vendor_id),
    }
    return {
      ...entry,
      credit_account: accountingHelpers.getCreditAccount(entry),
      debit_account: accountingHelpers.getDebitAccount(entry),
      is_ready_to_sync: accountingHelpers.isEntryReadyToSync(entry, customSegments)
    };
  },

  formatVendor: (vendor) => {
    return {
      ...vendor,
      is_ready_to_sync: accountingHelpers.isVendorReadyToSync(vendor)
    };
  },

  isBillInvoiceSyncStatusError: (status) => status === billInvoiceSyncStatusConstants.ERROR,

  isEntrySyncStatusError: (status) => status === journalEntrySyncStatusConstants.ERROR,

  isVendorSyncStatusError: (status) => status === vendorSyncStatusConstants.ERROR,

  showAlert: (type, message, duration = 4000, offset = 350) => {
    if (type === 'success') alertActions.success(message, duration, offset);
    if (type === 'warning') alertActions.warning(message, duration, offset);
    if (type === 'error') alertActions.error(message, duration);
  },

  getTableDataValue: (tableData, view, key, defaultValue) =>
    tableData.hasOwnProperty(view) ? tableData[view][key] : defaultValue,

  getTabProps: ({
    key,
    totalKey,
    tableData,
    expensesMap,
    loading,
    handleOnFilter,
    handleRowUpdate,
    handleOnTableChange,
    handleOnPaginate,
    getTableFiltersRightSideContent,
    total,
    tableFiltersProps = {},
    tableProps = {},
    ...rest
  }) => ({
    data: accountingHelpers.getTableDataValue(tableData, key, 'rows', []),
    tableFiltersProps: {
      rightSideContent: getTableFiltersRightSideContent(key),
      ...tableFiltersProps
    },
    loading: loading[key] || false,
    onFilter: (value) => handleOnFilter(key, value),
    onRowUpdate: (props) => handleRowUpdate({...props, view: key}),
    tableProps: {
      onChange: (pagination, filters, sorter, extra) => handleOnTableChange(key, pagination, filters, sorter, extra),
      pagination: tabHelpers.getTablePagination({handleOnPaginate, tableData, total, totalKey, view: key}),
      ...tableProps,
    },
    ...rest
  }),

  getJournalEntriesTabProps: ({
    customSegments = [],
    key,
    expensesMap = {},
    invoicesMap = {},
    tableData,
    vendors,
    ...rest
  }) => {
    const rows = accountingHelpers.getTableDataValue(tableData, key, 'rows', [])
      .map(entry => accountingHelpers.formatJournalEntry({
        customSegments, entry, expensesMap, invoicesMap, vendors
      }));

    return accountingHelpers.getTabProps({
      key,
      tableData: { ...tableData, [key]: { ...tableData[key], rows } },
      ...rest
    });
  },

  getInvoicesTabProps: ({
    customSegments = [],
    key,
    tableData,
    employees = [],
    vendors = [],
    ...rest
  }) => {
    let rows = accountingHelpers.getTableDataValue(tableData, key, 'rows', []).map(invoice =>
      accountingHelpers.formatInvoice({ customSegments, invoice, vendors, employees })
    );

    return accountingHelpers.getTabProps({
      key,
      tableData: { ...tableData, [key]: { ...tableData[key], rows } },
      ...rest
    });
  },

  getTableRowSelection: ({ onChange, syncStatus, selectedRowKeys = [] }) => ({
    getCheckboxProps: (record) => ({
      disabled: syncStatus !== undefined
        ? record.sync_status !== syncStatus
        : !record.is_ready_to_sync,
    }),
    selectedRowKeys,
    onChange: (rows) => onChange?.(rows),
  }),

  isMissingInvoice: (record) => {
    const isExpenseType = record?.expense?.expense_type === expenseTypesConstants.INVOICE;
    const isAccountingInvoice = Boolean(record?.vendor_bill?.remote_id);
    return record?.vendor_id && isExpenseType && !isAccountingInvoice;
  },

  renderReadyToSyncIcon: (record) => record?.is_ready_to_sync ? (
    <Tooltip title='Ready to sync'>
      <StyledCheckedCircleIconContainer>
        <CheckIcon />
      </StyledCheckedCircleIconContainer>
    </Tooltip>
  ) : '',

  renderInvoicePreview: (value, variant = 'backend') => value ? (
    <StyledJournalEntriesTabsIconSpace>
      <InvoicePreviewWithIcon invoiceId={value} variant={variant} />
    </StyledJournalEntriesTabsIconSpace>
  ) : '',

  renderJEInvoicePreview: (invoice) => {
    if (!invoice) return '';

    const invoiceId = invoice?.invoice_id;
    const invoiceFileUrl = invoice?.invoice_file_url;

    if (invoiceId) {
      return (
        <StyledJournalEntriesTabsIconSpace>
          <InvoicePreviewWithIcon invoiceId={invoiceId} />
        </StyledJournalEntriesTabsIconSpace>
      )
    }

    if (invoiceFileUrl) {
      return (
        <StyledJournalEntriesTabsIconSpace>
          <InvoiceFilePreviewWithIcon fileUrl={invoiceFileUrl} />
        </StyledJournalEntriesTabsIconSpace>
      )
    }

    return '';
  },

  getHistoryTabFilterProps: () => ({
    dateFilterName: 'posting_date',
    filterFields: ['search', 'status', 'daterange']
  }),

  handleSyncItems: ({ids = [], loadData, onSync, onSuccess, setSyncButtonLoading, trans}) => {
    if (!ids.length) {
      accountingHelpers.showAlert('warning', trans('validation.noItemsReadyToSync'));
      return;
    }

    setSyncButtonLoading(true);

    onSync({
      ids,
      successCallback: (response = {}) => {
        if (!response.success) {
          accountingHelpers.showAlert('error', response.message || trans('validation.failedToSendItemsToTheErp'));
        } else {
          onSuccess?.();
          loadData({ initial: true, view: 'synced' });
          accountingHelpers.showAlert('success', trans('validation.itemsHaveBeenSentToErp'));
        }
        setSyncButtonLoading(false);
      },
      errorCallback: (error) => {
        setSyncButtonLoading(false);
        accountingHelpers.showAlert('error', error?.message || trans('validation.failedToSendItemsToTheErp'));
      }
    });
  },

  getInvoiceLinkIcon: ({id, t} = {}) => {
    if (!id) return null;
    const invoiceUrl = invoicesHelpers.getInvoiceDetailsLink(id);

    return (
      <Tooltip title={t?.('seeAttachment')}>
        <Link to={invoiceUrl}>
          <StyledMissingErpTabIcon className='external-link-icon'>
            <ExternalLinkIcon />
          </StyledMissingErpTabIcon>
        </Link>
      </Tooltip>
    )
  },

  getCustomSegmentColumns: ({columns, getSelect, linesPropName = 'lines'} = {}) =>
    columns.map(({levels, name, options, ...rest}) => ({
      ...rest,
      render: (_, record) => getSelect?.({ defaultValue: accountingHelpers.getValue({ linesPropName, levels, name, record }), levels, record, options, propName: name }),
    })),

  updateTableDataWithVendors: ({setTableData, formatFunction, vendors = []}) => {
    // If there are no vendors or no table rows, nothing to do
    const hasVendors = vendors && vendors.length > 0;
    if (!hasVendors) return;

    setTableData(prevState => {
      const updatedTabs = Object.keys(prevState).reduce((acc, key) => {
        const tab = prevState[key];
        if (!tab || !Array.isArray(tab.rows)) {
          acc[key] = tab;
          return acc;
        }
        acc[key] = {
          ...tab,
          rows: tab.rows.map(row => formatFunction?.(row) || row)
        };
        return acc;
      }, {});
      return { ...prevState, ...updatedTabs };
    });
  }
}
